import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  sendEmailVerification as fbSendEmailVerification,
  sendPasswordResetEmail,
  confirmPasswordReset as fbConfirmPasswordReset,
  verifyPasswordResetCode as fbVerifyPasswordResetCode,
  applyActionCode as fbApplyActionCode,
  updatePassword as fbUpdatePassword,
  updateProfile,
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider,
  type User,
} from 'firebase/auth';
import { auth, ACTION_URL } from '../services/api/firebase';
import { reportReferralConversion } from '../lib/referral';

interface AuthContextValue {
  /** The raw Firebase user, or null when signed out. */
  user: User | null;
  /** True until the initial auth state has resolved. */
  loading: boolean;
  /** Convenience flags (kept reactive across reloadUser/updateProfile). */
  isAuthenticated: boolean;
  emailVerified: boolean;

  signUp: (email: string, password: string, displayName?: string) => Promise<User>;
  signIn: (email: string, password: string) => Promise<User>;
  signOut: () => Promise<void>;

  resetPassword: (email: string) => Promise<void>;
  confirmPasswordReset: (oobCode: string, newPassword: string) => Promise<void>;
  verifyResetCode: (oobCode: string) => Promise<string>;
  applyActionCode: (oobCode: string) => Promise<void>;

  sendEmailVerification: () => Promise<void>;
  reloadUser: () => Promise<void>;

  updatePassword: (newPassword: string) => Promise<void>;
  updateDisplayName: (displayName: string) => Promise<void>;

  reauthenticate: (password: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // Bumped after in-place mutations (reload, updateProfile) so consumers re-render
  // and read the latest values off the same Firebase User instance.
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  // Persist auth across reloads via Firebase's own session handling.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (next) => {
      setUser(next);
      setLoading(false);
    });
    return unsub;
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, displayName?: string) => {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) await updateProfile(cred.user, { displayName });
      await fbSendEmailVerification(cred.user, { url: ACTION_URL });
      setUser(cred.user);
      bump();
      reportReferralConversion('signup').catch(() => undefined);
      return cred.user;
    },
    [bump],
  );

  const signIn = useCallback(async (email: string, password: string) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    setUser(cred.user);
    return cred.user;
  }, []);

  const signOut = useCallback(async () => {
    await fbSignOut(auth);
    setUser(null);
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    await sendPasswordResetEmail(auth, email, { url: ACTION_URL });
  }, []);

  const confirmPasswordReset = useCallback(async (oobCode: string, newPassword: string) => {
    await fbConfirmPasswordReset(auth, oobCode, newPassword);
  }, []);

  const verifyResetCode = useCallback((oobCode: string) => fbVerifyPasswordResetCode(auth, oobCode), []);

  const applyActionCode = useCallback(
    async (oobCode: string) => {
      await fbApplyActionCode(auth, oobCode);
      if (auth.currentUser) {
        await auth.currentUser.reload();
        setUser(auth.currentUser);
        bump();
      }
    },
    [bump],
  );

  const sendEmailVerification = useCallback(async () => {
    if (!auth.currentUser) throw new Error('You must be signed in to verify your email.');
    await fbSendEmailVerification(auth.currentUser, { url: ACTION_URL });
  }, []);

  const reloadUser = useCallback(async () => {
    if (!auth.currentUser) return;
    await auth.currentUser.reload();
    setUser(auth.currentUser);
    bump();
  }, [bump]);

  const updatePassword = useCallback(
    async (newPassword: string) => {
      if (!auth.currentUser) throw new Error('You must be signed in.');
      await fbUpdatePassword(auth.currentUser, newPassword);
      bump();
    },
    [bump],
  );

  const updateDisplayName = useCallback(
    async (displayName: string) => {
      if (!auth.currentUser) throw new Error('You must be signed in.');
      await updateProfile(auth.currentUser, { displayName });
      setUser(auth.currentUser);
      bump();
    },
    [bump],
  );

  const reauthenticate = useCallback(async (password: string) => {
    if (!auth.currentUser?.email) throw new Error('You must be signed in.');
    const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
    await reauthenticateWithCredential(auth.currentUser, credential);
  }, []);

  const deleteAccount = useCallback(async () => {
    if (!auth.currentUser) throw new Error('You must be signed in.');
    await deleteUser(auth.currentUser);
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      emailVerified: !!user?.emailVerified,
      signUp,
      signIn,
      signOut,
      resetPassword,
      confirmPasswordReset,
      verifyResetCode,
      applyActionCode,
      sendEmailVerification,
      reloadUser,
      updatePassword,
      updateDisplayName,
      reauthenticate,
      deleteAccount,
    }),
    // `user` identity + the `version` bump cover all reactive reads (the latter
    // forces a new value after in-place mutations like reload/updateProfile).
    [user, version, loading, signUp, signIn, signOut, resetPassword, confirmPasswordReset, verifyResetCode, applyActionCode, sendEmailVerification, reloadUser, updatePassword, updateDisplayName, reauthenticate, deleteAccount],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return ctx;
}
